import { useMemo, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { api, qs, useApi, waitForJob } from "../lib/api";
import { bytes, fullDate, when } from "../lib/format";
import { Button, cx, ErrorNote, Modal, Spinner, toast } from "../components/ui";

interface UserRow {
  email: string;
  name: string;
  sub: string;
  lastSeen: string;
  usageBytes: number;
  usageAt: string | null;
  sharesGranted: number;
}
interface AuditRow {
  ts: string;
  email: string;
  action: string;
  path?: string;
  ownerSub?: string;
  detail?: Record<string, unknown>;
  ip?: string;
}

/** Usage and the activity log. By design there's no way here to open anyone's files. */
export default function Admin() {
  const [tab, setTab] = useState<"users" | "audit">("users");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
      <h1 className="text-lg font-semibold">Admin</h1>
      <p className="text-sm text-muted mt-1">Usage and activity. Admins can't open other people's files.</p>
      <div className="flex gap-1 mt-4 border-b border-line" role="tablist">
        {(["users", "audit"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={cx("px-3 h-10 text-sm font-medium border-b-2 -mb-px", tab === t ? "border-accent text-accent-text" : "border-transparent text-muted hover:text-ink")}>
            {t === "users" ? "Users" : "Activity log"}
          </button>
        ))}
      </div>
      <div className="mt-4">{tab === "users" ? <Users /> : <Audit />}</div>
    </div>
  );
}

function Users() {
  const users = useApi<{ users: UserRow[]; quotaBytes: number }>("/admin/users");
  const [purge, setPurge] = useState<UserRow | null>(null);
  if (users.error) return <ErrorNote>{users.error}</ErrorNote>;
  if (!users.data) return <Spinner />;
  const rows = [...users.data.users].sort((a, b) => b.usageBytes - a.usageBytes);
  const total = rows.reduce((n, u) => n + u.usageBytes, 0);
  return (
    <>
      <p className="text-sm text-muted mb-3 tabular">
        {rows.length} people · {bytes(total)} stored. Usage updates when each person opens ShareFile.
      </p>
      <div className="border border-line rounded-xl bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-muted text-xs text-left">
            <tr>
              <th className="px-4 py-2.5 font-medium">Person</th>
              <th className="px-4 py-2.5 font-medium text-right">Storage</th>
              <th className="px-4 py-2.5 font-medium text-right">Shares</th>
              <th className="px-4 py-2.5 font-medium text-right">Last signed in</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((u) => {
              const pct = (u.usageBytes / users.data!.quotaBytes) * 100;
              return (
                <tr key={u.email}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted">{u.email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular">
                    {bytes(u.usageBytes)}
                    <div className="h-1 mt-1 rounded-full bg-surface-2 w-24 ml-auto overflow-hidden">
                      <div className={cx("h-full", pct > 90 ? "bg-bad" : "bg-accent")} style={{ width: `${Math.min(100, Math.max(pct, 1))}%` }} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular">{u.sharesGranted}</td>
                  <td className="px-4 py-2.5 text-right text-muted" title={fullDate(u.lastSeen)}>
                    {when(u.lastSeen)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="ghost" onClick={() => setPurge(u)} aria-label={`Delete ${u.email}'s files`} title="Delete this person's files">
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {purge && <PurgeDialog user={purge} onClose={() => setPurge(null)} onDone={() => void users.reload()} />}
    </>
  );
}

function PurgeDialog({ user, onClose, onDone }: { user: UserRow; onClose: () => void; onDone: () => void }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = typed.trim().toLowerCase() === user.email;
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { jobId } = await api<{ jobId: string }>("/admin/purge", { body: { email: user.email, confirmEmail: typed } });
      const j = await waitForJob(jobId);
      if (j.status === "failed") throw new Error(j.error ?? "Delete failed");
      toast(`Deleted ${user.name}'s files`);
      onDone();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Delete ${user.name}'s files?`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!ok} busy={busy} onClick={run}>
            Delete all files
          </Button>
        </>
      }
    >
      <div className="text-sm text-ink-2 space-y-2">
        <p>
          This deletes all {bytes(user.usageBytes)} in {user.email}'s area and removes every share they made. It's logged.
        </p>
        <p>Files can be recovered for 30 days by restoring their versions in S3 (ask the AWS admin). After that they're gone.</p>
        <label className="block font-medium pt-2">Type {user.email} to confirm</label>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} className="w-full h-10 rounded-lg border border-line bg-surface px-3" autoComplete="off" />
        {error && <p className="text-bad">{error}</p>}
      </div>
    </Modal>
  );
}

function Audit() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [who, setWho] = useState("");
  const [action, setAction] = useState("");
  const log = useApi<{ entries: AuditRow[] }>(`/admin/audit?${qs({ month })}`);
  const entries = useMemo(
    () => (log.data?.entries ?? []).filter((e) => (!who || e.email?.includes(who.toLowerCase())) && (!action || e.action.startsWith(action))),
    [log.data, who, action],
  );
  const actions = useMemo(() => [...new Set((log.data?.entries ?? []).map((e) => e.action.split(".")[0]))].sort(), [log.data]);
  const csv = () => {
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["time", "email", "action", "path", "detail", "ip"].join(","), ...entries.map((e) => [e.ts, e.email, e.action, e.path, e.detail ? JSON.stringify(e.detail) : "", e.ip].map(esc).join(","))];
    const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sharefile-activity-${month}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <label className="text-xs text-muted">
          Month
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="block h-9 rounded-lg border border-line bg-surface px-2 text-sm text-ink mt-1" />
        </label>
        <label className="text-xs text-muted">
          Person
          <input value={who} onChange={(e) => setWho(e.target.value)} placeholder="email contains…" className="block h-9 rounded-lg border border-line bg-surface px-2 text-sm text-ink mt-1" />
        </label>
        <label className="text-xs text-muted">
          Action
          <select value={action} onChange={(e) => setAction(e.target.value)} className="block h-9 rounded-lg border border-line bg-surface px-2 text-sm text-ink mt-1">
            <option value="">All</option>
            {actions.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <Button className="ml-auto" onClick={csv} disabled={!entries.length}>
          <Download className="size-4" /> Export CSV
        </Button>
      </div>
      {log.error ? (
        <ErrorNote>{log.error}</ErrorNote>
      ) : !log.data ? (
        <Spinner />
      ) : !entries.length ? (
        <p className="text-sm text-muted py-6">No activity for this filter.</p>
      ) : (
        <div className="border border-line rounded-xl bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-muted text-xs text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">When</th>
                <th className="px-4 py-2.5 font-medium">Who</th>
                <th className="px-4 py-2.5 font-medium">Action</th>
                <th className="px-4 py-2.5 font-medium">Item</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {entries.map((e, i) => (
                <tr key={`${e.ts}-${i}`}>
                  <td className="px-4 py-2 text-muted whitespace-nowrap" title={fullDate(e.ts)}>
                    {new Date(e.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-2">{e.email || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs">{e.action}</span>
                  </td>
                  <td className="px-4 py-2 text-muted max-w-sm truncate" title={e.detail ? JSON.stringify(e.detail) : undefined}>
                    {e.path ?? summary(e.detail)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function summary(d?: Record<string, unknown>): string {
  if (!d) return "";
  if (Array.isArray(d.paths)) return (d.paths as string[]).join(", ");
  if (d.targetEmail) return String(d.targetEmail);
  return "";
}
